/**
 * Maps each primitive file (basename without `.tsx`) to `ui-skeleton.css` shape + line count.
 * `voidLike`: render a single placeholder `<span>` instead of the native element when skeleton is active.
 */
export type SkeletonUiEntry = {
  shape: string;
  lines: number;
  voidLike?: boolean;
};

export const SKELETON_BY_COMPONENT: Record<string, SkeletonUiEntry> = {
  Abbr: { shape: 'abbr', lines: 1 },
  Address: { shape: 'address', lines: 1 },
  Anchor: { shape: 'anchor', lines: 1 },
  Article: { shape: 'article', lines: 1 },
  Aside: { shape: 'aside', lines: 1 },
  Base: { shape: 'div', lines: 1 },
  Bdi: { shape: 'span', lines: 1 },
  Bdo: { shape: 'span', lines: 1 },
  Blockquote: { shape: 'blockquote', lines: 1 },
  Bold: { shape: 'strong', lines: 1 },
  Caption: { shape: 'table-caption', lines: 1 },
  Cite: { shape: 'cite', lines: 1 },
  Code: { shape: 'code', lines: 1 },
  Data: { shape: 'data', lines: 1 },
  Dd: { shape: 'dd', lines: 1 },
  Del: { shape: 'del', lines: 1 },
  Dfn: { shape: 'dfn', lines: 1 },
  Em: { shape: 'em', lines: 1 },
  H1: { shape: 'h1', lines: 3 },
  H2: { shape: 'h2', lines: 1 },
  H3: { shape: 'h3', lines: 1 },
  H4: { shape: 'h4', lines: 1 },
  H5: { shape: 'h5', lines: 1 },
  H6: { shape: 'h6', lines: 1 },
  I: { shape: 'i', lines: 1 },
  Ins: { shape: 'ins', lines: 1 },
  Kbd: { shape: 'kbd', lines: 1 },
  Label: { shape: 'label', lines: 1 },
  Legend: { shape: 'legend', lines: 1 },
  Mark: { shape: 'mark', lines: 1 },
  P: { shape: 'p', lines: 4 },
  Pre: { shape: 'pre', lines: 1 },
  Q: { shape: 'span', lines: 1 },
  Rp: { shape: 'span', lines: 1 },
  Rt: { shape: 'span', lines: 1 },
  S: { shape: 's', lines: 1 },
  Samp: { shape: 'samp', lines: 1 },
  Slot: { shape: 'div', lines: 1 },
  Small: { shape: 'small', lines: 1 },
  Span: { shape: 'span', lines: 1 },
  Strong: { shape: 'strong', lines: 1 },
  Sub: { shape: 'span', lines: 1 },
  Summary: { shape: 'span', lines: 1 },
  Sup: { shape: 'span', lines: 1 },
  Time: { shape: 'time', lines: 1 },
  U: { shape: 'u', lines: 1 },
  Var: { shape: 'var', lines: 1 },
  Area: { shape: 'area', lines: 1, voidLike: true },
  Audio: { shape: 'audio-embed', lines: 1 },
  Canvas: { shape: 'canvas', lines: 1 },
  Details: { shape: 'details', lines: 1 },
  Dialog: { shape: 'dialog', lines: 1 },
  Embed: { shape: 'embed', lines: 1, voidLike: true },
  Iframe: { shape: 'iframe', lines: 1, voidLike: true },
  Map: { shape: 'map', lines: 1 },
  Object: { shape: 'object', lines: 1 },
  Source: { shape: 'span', lines: 1, voidLike: true },
  Track: { shape: 'span', lines: 1, voidLike: true },
  Video: { shape: 'video', lines: 1, voidLike: true },
  Button: { shape: 'btn', lines: 1 },
  Datalist: { shape: 'datalist', lines: 1 },
  Fieldset: { shape: 'fieldset', lines: 1 },
  Form: { shape: 'form', lines: 1 },
  Input: { shape: 'input', lines: 1, voidLike: true },
  Meter: { shape: 'meter', lines: 1, voidLike: true },
  OptGroup: { shape: 'optgroup', lines: 1 },
  Option: { shape: 'option', lines: 1 },
  Output: { shape: 'output', lines: 1 },
  Progress: { shape: 'progress', lines: 1, voidLike: true },
  Select: { shape: 'select', lines: 1, voidLike: true },
  Textarea: { shape: 'textarea', lines: 1, voidLike: true },
  Menu: { shape: 'menu', lines: 1 },
  Dl: { shape: 'dl', lines: 1 },
  Dt: { shape: 'dt', lines: 1 },
  Li: { shape: 'li', lines: 1 },
  Ol: { shape: 'ol', lines: 1 },
  Ul: { shape: 'ul', lines: 1 },
  Figcaption: { shape: 'figcaption', lines: 1 },
  Figure: { shape: 'figure', lines: 1 },
  Img: { shape: 'img', lines: 1 },
  Picture: { shape: 'picture', lines: 1 },
  Ruby: { shape: 'ruby', lines: 1 },
  Wbr: { shape: 'wbr', lines: 1, voidLike: true },
  Footer: { shape: 'footer', lines: 1 },
  Header: { shape: 'header', lines: 1 },
  HGroup: { shape: 'hgroup', lines: 1 },
  Nav: { shape: 'nav', lines: 1 },
  Search: { shape: 'search', lines: 1 },
  Hr: { shape: 'hr', lines: 1, voidLike: true },
  Template: { shape: 'div', lines: 1 },
  Col: { shape: 'div', lines: 1, voidLike: true },
  ColGroup: { shape: 'div', lines: 1, voidLike: true },
  Table: { shape: 'table', lines: 1, voidLike: true },
  TBody: { shape: 'div', lines: 1, voidLike: true },
  Td: { shape: 'td', lines: 1, voidLike: true },
  Th: { shape: 'th', lines: 1, voidLike: true },
  Thead: { shape: 'thead', lines: 1, voidLike: true },
  Tfoot: { shape: 'tfoot', lines: 1, voidLike: true },
  Tr: { shape: 'tr', lines: 1, voidLike: true },
};

export function skeletonConfigForFile(basename: string): SkeletonUiEntry {
  const key = basename.replace(/\.tsx$/i, '');
  return (
    SKELETON_BY_COMPONENT[key] ?? {
      shape: 'div',
      lines: 1,
    }
  );
}
